import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaShieldAlt, FaLock, FaUserShield, FaDatabase, FaCookie, FaGlobe } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const sections = [
    {
      icon: FaDatabase,
      title: 'Information We Collect',
      content: [
        {
          subtitle: 'Personal Information',
          text: 'We may collect personal information that you voluntarily provide when contacting us, including your name, email address, phone number, company name, and project details through our contact forms.'
        },
        {
          subtitle: 'Technical Information',
          text: 'When you visit our website, we automatically collect certain technical information including IP address, browser type, device information, operating system, referring URLs, and browsing behavior on our site.'
        },
        {
          subtitle: 'Communication Data',
          text: 'We retain records of communications with you, including emails, contact form submissions, and any other correspondence related to our products and services.'
        }
      ]
    },
    {
      icon: FaCookie,
      title: 'Cookies & Tracking Technologies',
      content: [
        {
          subtitle: 'Essential Cookies',
          text: 'We use essential cookies necessary for the proper functioning of our website. These cookies enable core functionality such as security, network management, and accessibility.'
        },
        {
          subtitle: 'Analytics Cookies',
          text: 'We may use analytics cookies to understand how visitors interact with our website, helping us improve user experience and content relevance. These cookies collect aggregated, anonymous data.'
        },
        {
          subtitle: 'Cookie Control',
          text: 'You can control and manage cookies through your browser settings. However, disabling certain cookies may affect the functionality of our website.'
        }
      ]
    },
    {
      icon: FaLock,
      title: 'How We Use Your Information',
      content: [
        {
          subtitle: 'Service Provision',
          text: 'We use your information to respond to inquiries, process requests for product information, provide technical support, and deliver our defense technology services.'
        },
        {
          subtitle: 'Communication',
          text: 'We may use your contact information to send important updates about our products, respond to your queries, and provide information about our defense solutions that may interest you.'
        },
        {
          subtitle: 'Legal Compliance',
          text: 'We process information as required by applicable laws, regulations, and legal obligations, including export control compliance for defense-related technologies.'
        }
      ]
    },
    {
      icon: FaUserShield,
      title: 'Data Protection & Security',
      content: [
        {
          subtitle: 'Security Measures',
          text: 'We implement industry-standard security measures including encryption, firewalls, secure servers, and access controls to protect your personal information from unauthorized access, alteration, or disclosure.'
        },
        {
          subtitle: 'Data Retention',
          text: 'We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy or as required by applicable laws and regulations.'
        },
        {
          subtitle: 'Third-Party Access',
          text: 'We do not sell, trade, or rent your personal information to third parties. We may share information with trusted service providers who assist us in operating our website and business, subject to confidentiality agreements.'
        }
      ]
    },
    {
      icon: FaGlobe,
      title: 'International Data Transfers',
      content: [
        {
          subtitle: 'Cross-Border Data',
          text: 'As a defense technology company based in India, your data is primarily processed and stored in India. If you access our services from outside India, your information may be transferred to and processed in India.'
        },
        {
          subtitle: 'Compliance Standards',
          text: 'We adhere to applicable data protection standards and regulations for international data transfers. We ensure appropriate safeguards are in place for any cross-border data processing.'
        }
      ]
    },
    {
      icon: FaShieldAlt,
      title: 'Your Rights',
      content: [
        {
          subtitle: 'Access & Correction',
          text: 'You have the right to request access to your personal data we hold and to request corrections if any information is inaccurate or incomplete.'
        },
        {
          subtitle: 'Deletion & Restriction',
          text: 'You may request deletion of your personal data or restriction of its processing, subject to legal requirements and our legitimate business interests.'
        },
        {
          subtitle: 'Opt-Out',
          text: 'You can opt out of marketing communications at any time by contacting us or using the unsubscribe link in our emails. We will process your request promptly.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-primary-950">
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-5" />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-accent/3 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <FaShieldAlt className="text-4xl text-accent mx-auto mb-4" />
            <span className="text-accent font-display text-xs tracking-[0.3em]">LEGAL</span>
            <h1 className="text-3xl md:text-5xl font-display font-bold text-white mt-3 mb-3">
              Privacy <span className="text-accent">Policy</span>
            </h1>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              Last updated: January 2025
            </p>
            <div className="golden-line mx-auto mt-6" />
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="relative pb-20" ref={ref}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="glass-card p-6 md:p-8 mb-6"
          >
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              At <span className="text-accent font-semibold">Sronzer</span>, we are committed to protecting 
              your privacy and ensuring the security of your personal information. This Privacy Policy explains how 
              we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed">
              By accessing or using our website, you agree to the terms of this Privacy Policy. If you do not agree 
              with our policies and practices, please do not use our website or services.
            </p>
          </motion.div>

          {/* Policy Sections */}
          <div className="space-y-6">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-card p-6 md:p-8"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <section.icon className="text-xl text-accent" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-display font-bold text-white">
                    {section.title}
                  </h2>
                </div>

                {section.content.map((item, idx) => (
                  <div key={idx} className="ml-12 mb-4 last:mb-0">
                    <h3 className="text-accent font-display font-bold text-sm mb-2">
                      {item.subtitle}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                ))}
              </motion.div>
            ))}
          </div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="glass-card p-6 md:p-8 mt-8"
          >
            <h2 className="text-xl font-display font-bold text-white mb-4">Contact Us</h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="space-y-2 text-sm text-gray-300">
              <p><span className="text-accent">Email:</span> connect@tavasindustries.com</p>
              <p><span className="text-accent">Phone:</span> +91 7440755715</p>
              <p><span className="text-accent">Address:</span> No.303 A(5), Akriti Business Center, ABC Mall, Bhopal, MP 462026</p>
            </div>
          </motion.div>

          {/* Bottom Navigation */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <Link 
              to="/terms"
              className="text-accent hover:text-accent-light transition-colors font-display text-sm tracking-wider"
            >
              VIEW TERMS OF SERVICE →
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;